import { useEffect } from 'react'
import { Box, Grid, styled } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'app/state'
import { PaginatedResponse } from 'features/common'
import Loader from 'features/common/ui/utils/Loader'

import { setEditMapError } from 'features/maps/state/mapsSlice'

import MapsEmpty from './MapsEmpty'
import MapsSample from '../MapsSample/MapsSample'
import EmptyState from 'features/common/ui/utils/EmptyState'
import NoMapIcon from 'assets/icons/carto-icon-maps-no-map.svg?react'
import { FormattedMessage } from 'react-intl'
import { useCartoDW } from 'features/connections/hooks/connectionsHooks'
import NoConnectionsOrCdwPlaceholder from 'features/connections/ui/NoConnectionsOrCdwPlaceholder'
import useNotifications from 'features/notifications/ui/useNotifications'
import useUserRoles from 'features/account/state/useUserRoles'
import { useGovernanceConnections } from 'features/connections/hooks/useGovernanceConnections'
import { Map } from '../../types'
import MapsGalleryList from './MapsGalleryList'
import { InfiniteData } from 'react-query'

const RootGrid = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'withApps'
})<{
  withApps?: boolean
}>(({ withApps, theme }) => ({
  position: 'relative',
  minHeight: withApps ? theme.spacing(43) : undefined,

  '& .infinite-scroll-component__outerdiv': {
    width: '100%'
  }
}))

const NotFound = styled(Box)(() => ({
  width: '100%',
  display: 'flex'
}))

interface Props {
  data?: InfiniteData<PaginatedResponse<Map>>
  isLoading: boolean
  hasNextPage?: boolean
  fetchNextPage(): void
  hasActiveFilters: boolean
}

function MapsGallery({ hasNextPage, fetchNextPage, isLoading, data, hasActiveFilters }: Props) {
  const mapsCount = data?.pages?.[0]?.meta?.page?.total || 0
  const dataLength = (data?.pages.length || 1) * (data?.pages?.[0]?.data?.length || 96)
  const dispatch = useAppDispatch()
  const mapEditError = useAppSelector((state) => state.maps.mapEditError)

  const { showConnectionsBanner } = useCartoDW()
  const { sendErrorNotification, sendErrorNotificationLocalize } = useNotifications()
  const { userHasEditionRole } = useUserRoles()
  const { hasAvailableProviders, loading } = useGovernanceConnections()

  useEffect(() => {
    if (mapEditError) {
      if (mapEditError.error === 'This map is locked by someone else') {
        sendErrorNotificationLocalize('maps.errorCurrentMapLocked')
      } else {
        sendErrorNotification(mapEditError.error)
      }
      dispatch(setEditMapError(undefined))
    }
  }, [dispatch, mapEditError, sendErrorNotification, sendErrorNotificationLocalize])

  if (showConnectionsBanner && userHasEditionRole) {
    return <NoConnectionsOrCdwPlaceholder hasAvailableProviders={hasAvailableProviders && !loading} />
  }

  if (isLoading) {
    return (
      <Box mt={8} data-testid="general-loader">
        <Loader />
      </Box>
    )
  }

  if (mapsCount === 0 && hasActiveFilters) {
    return (
      <NotFound mb={1.5} mt={2}>
        <EmptyState tight={true} icon={NoMapIcon} caption={<FormattedMessage id="maps.filters.noResults" />} />
      </NotFound>
    )
  }
  if (mapsCount) {
    return (
      <MapsGalleryList next={fetchNextPage} dataLength={dataLength} hasMore={Boolean(hasNextPage)} mapsPages={data} />
    )
  }

  return (
    <RootGrid container spacing={3} withApps={mapsCount > 0 || isLoading}>
      <MapsEmpty />
      {userHasEditionRole && <MapsSample />}
    </RootGrid>
  )
}

export default MapsGallery
